import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { inventoryApi } from '../../api/inventory-api-instance';
import { ApiError } from '../../api/errors';
import { PartialFailureNotice } from '../../components/PartialFailureNotice';
import { SearchableSelect } from '../../components/SearchableSelect';
import {
  itemStatuses,
  type CreateItemInput,
  type ItemStatus,
  type Part,
  type Site,
} from '../../domain/inventory';
import { statusLabels } from '../../domain/status';
import { FULL_FLEET_ITEMS_QUERY_KEY } from '../dashboard/aggregations';
import { humanizeIdentifier, sortByDisplayLabel } from '../../lib/formatting';
import { siteDisplayName, siteLabel, siteSearchAliases } from '../../lib/site-label';
import { cleanItemId, itemIdProblem } from '../../domain/item-id';

interface NewItemDrawerProps {
  parts: Part[];
  sites: Site[];
  onClose: () => void;
  onCreated: (itemId: string) => void;
}

interface CreateDraft {
  id: string;
  partId: string;
  siteId: string;
  locationSiteId: string;
  location: string;
  description: string;
  identity: string;
  hardwareRevision: string;
  status: ItemStatus | '';
  notes: string;
  needsAttention: boolean;
  attentionReason: string;
}

const EMPTY_DRAFT: CreateDraft = {
  id: '',
  partId: '',
  siteId: '',
  locationSiteId: '',
  location: '',
  description: '',
  identity: '',
  hardwareRevision: '',
  status: '',
  notes: '',
  needsAttention: false,
  attentionReason: '',
};
const alphabetizedStatuses = sortByDisplayLabel(itemStatuses, (status) => statusLabels[status]);

function toInput(draft: CreateDraft): CreateItemInput {
  return {
    id: cleanItemId(draft.id),
    partId: draft.partId,
    siteId: draft.siteId,
    ...(draft.locationSiteId ? { locationSiteId: draft.locationSiteId } : {}),
    ...(draft.location.trim() ? { location: draft.location.trim() } : {}),
    ...(draft.description.trim() ? { description: draft.description.trim() } : {}),
    ...(draft.identity.trim() ? { identity: draft.identity.trim() } : {}),
    ...(draft.hardwareRevision.trim() ? { hardwareRevision: draft.hardwareRevision.trim() } : {}),
    ...(draft.status ? { status: draft.status } : {}),
    ...(draft.notes.trim() ? { notes: draft.notes.trim() } : {}),
    needsAttention: draft.needsAttention,
    attentionReason: draft.needsAttention ? draft.attentionReason.trim() : '',
  };
}

export function NewItemDrawer({ parts, sites, onClose, onCreated }: NewItemDrawerProps) {
  const queryClient = useQueryClient();
  const sortedParts = useMemo(
    () =>
      sortByDisplayLabel(
        parts,
        (part) => `${humanizeIdentifier(part.type)} ${humanizeIdentifier(part.build)}`,
      ),
    [parts],
  );
  const sortedSites = useMemo(
    () => sortByDisplayLabel(sites, (site) => siteDisplayName(site)),
    [sites],
  );
  const [draft, setDraft] = useState<CreateDraft>(EMPTY_DRAFT);
  const [reviewing, setReviewing] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [unknownOutcome, setUnknownOutcome] = useState(false);

  const mutation = useMutation({
    mutationFn: (input: CreateItemInput) => inventoryApi.createItem(input),
    onSuccess: async (created) => {
      // See EditItemDrawer's onSuccess for why the full-fleet query is
      // invalidated separately with refetchType: 'none'.
      await queryClient.invalidateQueries({ queryKey: ['items'] });
      void queryClient.invalidateQueries({
        queryKey: FULL_FLEET_ITEMS_QUERY_KEY,
        refetchType: 'none',
      });
      onCreated(created.id);
    },
    onError: (error: unknown) => {
      if (error instanceof ApiError && error.code === 'NETWORK') {
        setUnknownOutcome(true);
        return;
      }
      setFormError(error instanceof Error ? error.message : 'The item could not be created.');
    },
  });

  const assignedSite = sites.find((site) => site.id === draft.siteId);
  const locationSite = sites.find((site) => site.id === (draft.locationSiteId || draft.siteId));
  const part = parts.find((candidate) => candidate.id === draft.partId);

  function update<K extends keyof CreateDraft>(key: K, value: CreateDraft[K]): void {
    setDraft((current) => ({ ...current, [key]: value }));
  }

  function handleContinue(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setFormError(null);
    if (!draft.id.trim() || !draft.partId || !draft.siteId) {
      setFormError('Item ID, part definition, and assigned site are required.');
      return;
    }
    const idProblem = itemIdProblem(cleanItemId(draft.id));
    if (idProblem) {
      setFormError(idProblem);
      return;
    }
    if (draft.needsAttention && !draft.attentionReason.trim()) {
      setFormError('Add a reason before marking this item as needing attention.');
      return;
    }
    setReviewing(true);
  }

  return (
    <div className="drawer-overlay" role="presentation" onClick={onClose}>
      {reviewing ? (
        <div
          className="drawer drawer-wide"
          role="dialog"
          aria-label="Review new item"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="drawer-heading">
            <div>
              <span className="step-label">Step 2 of 2</span>
              <h2>Review new item</h2>
            </div>
            <button
              type="button"
              className="icon-button"
              onClick={onClose}
              aria-label="Close new item drawer"
            >
              ×
            </button>
          </div>
          <p className="drawer-version-note">
            Confirm the operational identity and location before creating the serialized record.
          </p>

          {formError ? (
            <p role="alert" className="form-error">
              {formError}
            </p>
          ) : null}
          {unknownOutcome ? (
            <PartialFailureNotice
              onCheckCurrentState={() => {
                void queryClient.invalidateQueries({ queryKey: ['items'] });
                onClose();
              }}
            />
          ) : null}

          <dl className="review-grid">
            <dt>Item ID</dt>
            <dd className="mono">{draft.id.trim()}</dd>
            <dt>Part definition</dt>
            <dd>
              {part
                ? `${humanizeIdentifier(part.type)} · ${humanizeIdentifier(part.build)}`
                : draft.partId}
            </dd>
            <dt>Assigned site</dt>
            <dd>{siteLabel(assignedSite, draft.siteId)}</dd>
            <dt>Current location</dt>
            <dd>{siteLabel(locationSite, draft.locationSiteId || draft.siteId)}</dd>
            <dt>Hardware revision</dt>
            <dd>{draft.hardwareRevision.trim() || 'Not recorded'}</dd>
            <dt>Status</dt>
            <dd>
              {draft.status ? statusLabels[draft.status] : 'Not recorded (data-quality flag)'}
            </dd>
            <dt>Attention</dt>
            <dd>{draft.needsAttention ? draft.attentionReason.trim() : 'No attention flag'}</dd>
          </dl>

          <div className="info-callout">
            The backend will create version 0 and record the modification time and actor. Current
            location defaults to the assigned site when no separate location is selected.
          </div>

          <div className="drawer-actions">
            <button type="button" onClick={() => setReviewing(false)} disabled={mutation.isPending}>
              Back
            </button>
            <button
              type="button"
              className="primary"
              onClick={() => {
                setFormError(null);
                setUnknownOutcome(false);
                mutation.mutate(toInput(draft));
              }}
              disabled={mutation.isPending}
            >
              {mutation.isPending ? 'Creating…' : 'Create item'}
            </button>
          </div>
        </div>
      ) : (
        <form
          className="drawer drawer-wide"
          aria-label="Create new item"
          onClick={(event) => event.stopPropagation()}
          onSubmit={handleContinue}
        >
          <div className="drawer-heading">
            <div>
              <span className="step-label">Step 1 of 2</span>
              <h2>New serialized item</h2>
            </div>
            <button
              type="button"
              className="icon-button"
              onClick={onClose}
              aria-label="Close new item drawer"
            >
              ×
            </button>
          </div>
          <p className="drawer-version-note">Required fields first.</p>

          {formError ? (
            <p role="alert" className="form-error">
              {formError}
            </p>
          ) : null}

          <label>
            Item ID
            <input
              value={draft.id}
              onChange={(event) => update('id', event.target.value)}
              placeholder="Unique serial or item ID"
              autoFocus
              required
            />
          </label>
          <label>
            Part definition
            <SearchableSelect
              ariaLabel="Part definition"
              onChange={(value) => update('partId', value)}
              options={[
                { value: '', label: 'Select a part…' },
                ...sortedParts.map((candidate) => ({
                  value: candidate.id,
                  label: `${humanizeIdentifier(candidate.type)} · ${humanizeIdentifier(candidate.build)}`,
                  aliases: [candidate.id, candidate.type, candidate.build],
                })),
              ]}
              placeholder="Select a part…"
              required
              value={draft.partId}
            />
          </label>
          <label>
            Assigned site
            <SearchableSelect
              ariaLabel="Assigned site"
              onChange={(value) => {
                update('siteId', value);
                if (!draft.locationSiteId) update('locationSiteId', '');
              }}
              options={[
                { value: '', label: 'Select an accountable site…' },
                ...sortedSites.map((site) => ({
                  value: site.id,
                  label: siteLabel(site),
                  aliases: siteSearchAliases(site),
                })),
              ]}
              placeholder="Select an accountable site…"
              required
              value={draft.siteId}
            />
          </label>

          <div className="form-divider">
            <span>Operational context</span>
          </div>

          <label>
            Current location site
            <SearchableSelect
              ariaLabel="Current location site"
              onChange={(value) => update('locationSiteId', value)}
              options={[
                { value: '', label: 'Same as assigned site' },
                ...sortedSites.map((site) => ({
                  value: site.id,
                  label: siteLabel(site),
                  aliases: siteSearchAliases(site),
                })),
              ]}
              placeholder="Same as assigned site"
              value={draft.locationSiteId}
            />
          </label>
          <label>
            Location detail
            <input
              value={draft.location}
              onChange={(event) => update('location', event.target.value)}
              placeholder="Zone, shelf, workbench, vessel…"
            />
          </label>
          <div className="form-row">
            <label>
              Status
              <SearchableSelect
                ariaLabel="Status"
                onChange={(value) => update('status', value as ItemStatus | '')}
                options={[
                  { value: '', label: 'Not recorded' },
                  ...alphabetizedStatuses
                    .filter((value) => value !== 'STATUS_UNSPECIFIED')
                    .map((value) => ({ value, label: statusLabels[value], aliases: [value] })),
                ]}
                placeholder="Not recorded"
                value={draft.status}
              />
            </label>
            <label>
              Identity
              <input
                value={draft.identity}
                onChange={(event) => update('identity', event.target.value)}
              />
            </label>
          </div>
          <label>
            Description
            <input
              value={draft.description}
              onChange={(event) => update('description', event.target.value)}
            />
          </label>
          <label>
            Hardware revision
            <input
              value={draft.hardwareRevision}
              onChange={(event) => update('hardwareRevision', event.target.value)}
            />
          </label>
          <label>
            Notes
            <textarea
              value={draft.notes}
              onChange={(event) => update('notes', event.target.value)}
              rows={3}
            />
          </label>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={draft.needsAttention}
              onChange={(event) => {
                update('needsAttention', event.target.checked);
                if (!event.target.checked) update('attentionReason', '');
              }}
            />
            Needs attention
          </label>
          {draft.needsAttention ? (
            <label>
              Attention reason
              <input
                value={draft.attentionReason}
                onChange={(event) => update('attentionReason', event.target.value)}
                required
              />
            </label>
          ) : null}

          <div className="drawer-actions">
            <button type="button" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="primary">
              Review item
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
